import Map from 'components/Map';
import { AlertCamerasLayer } from 'components/Map/layers/AlertCamerasLayer';
import GeoEventsLayer from 'components/Map/layers/GeoEventsLayer';
import { StructuredEvacuationsEditLayer } from 'components/Map/layers/StructuredEvacuationsLayer';
import { useLocation } from 'react-router-dom';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { DISABLE_MAP_LAYERS_FOR_FORMS } from 'components/Map/constants';
import { useCallback, useMemo } from 'react';
import {
  EvacZoneStatus,
  FormEvacZoneStatus,
  LayerEvacZone,
  MapLocation,
} from 'shared/types';
import { useFormContext } from 'react-hook-form';
import { parseZoomParam } from 'shared/utils';
import { useQueryParams } from 'hooks/useQueryParms';
import { IncidentMapStateUpdate, useMapState } from 'state/useMapState';
import { useWildfires } from 'features/wildfires/hooks/useWildfires';
import { useRegionEvacZonesForGeoEvent } from 'features/evacZones/hooks/useRegionEvacZonesForGeoEvent';
import {
  GeoEventFormQueryParams,
  GeoEventFormValues,
} from '../../GeoEventForm.types';
import { getDefaultMapCenter } from '../../GeoEventForm.utils';
import { GeoEventMarker } from '../../GeoEventMarker';

type GeoEventMapProps = {
  geoEvent?: Wildfire;
  zoomControlEnabled: boolean;
  isEvacZonesLayerVisible: boolean;
  evacZones: LayerEvacZone[];
  handleLocationChange: (
    location:
      | MapLocation
      | {
          lat: number;
          lng: number;
        }
      | null,
    urlAddress?: string | null,
  ) => Promise<void>;
};

export const GeoEventFormMap = (props: GeoEventMapProps): JSX.Element => {
  const {
    geoEvent,
    zoomControlEnabled,
    isEvacZonesLayerVisible,
    evacZones,
    handleLocationChange,
  } = props;
  const { search } = useLocation();
  const { setValue, watch } = useFormContext<GeoEventFormValues>();
  const [queryParams] = useQueryParams<GeoEventFormQueryParams>();
  const { setActiveMapBounds } = useMapState();
  const { wildfireEvents } = useWildfires();
  const { regionEvacZones } = useRegionEvacZonesForGeoEvent(geoEvent);

  const [
    name,
    address,
    acreage,
    containment,
    isComplexParent,
    isPrescribed,
    isFps,
    notificationType,
    status,
    lat,
    lng,
    evacZoneStatuses,
    temporarilyDisplayEvacZones,
    media,
  ] = watch([
    'name',
    'address',
    'acreage',
    'containment',
    'isComplexParent',
    'isPrescribed',
    'isFps',
    'notificationType',
    'status',
    'lat',
    'lng',
    'evacZoneStatuses',
    'temporarilyDisplayEvacZones',
    'media',
  ]);

  const onViewportChange = useCallback(
    (viewport: IncidentMapStateUpdate) => {
      setActiveMapBounds(viewport.bounds);
    },
    [setActiveMapBounds],
  );

  const handleEvacZoneClick = useCallback(
    (uid: string, nextStatus?: EvacZoneStatus) => {
      const evacZone = regionEvacZones.find((zone) => zone.uidV2 === uid);

      if (!evacZone) return;

      const filteredCurrentZones = evacZoneStatuses.filter(
        (eZS) => eZS.evacZone.uidV2 !== uid,
      );

      const geoEventEvacZone = geoEvent?.evacZoneStatuses.find(
        (eZS) => eZS.evacZone.uidV2 === uid,
      );

      if (nextStatus) {
        // Add
        const updatedZone: FormEvacZoneStatus = {
          evacZone: {
            ...evacZone,
            regionEvacZoneStyle: evacZone.region.evacZoneStyle,
          },
          status: nextStatus,
        };
        if (geoEventEvacZone) {
          updatedZone.prevStatus = geoEventEvacZone.status;
        }

        setValue('evacZoneStatuses', [...filteredCurrentZones, updatedZone]);
        return;
      }
      // Remove
      setValue('evacZoneStatuses', filteredCurrentZones);
    },
    [regionEvacZones, evacZoneStatuses, geoEvent?.evacZoneStatuses, setValue],
  );

  const initialZoom = parseZoomParam(queryParams.zoom ?? '');

  const searchParams = new URLSearchParams(search);
  const defaultMapCenter = getDefaultMapCenter({ searchParams, geoEvent });

  const searchBarReturnLink = geoEvent
    ? `/incident/${geoEvent.id}/edit`
    : '/incident/create';

  const cacheBuster = useMemo(() => Date.now(), []);

  const isActive = status === 'active';

  const currentGeoEvent = useMemo(() => {
    const geoEventWithFormValues: Partial<Wildfire> = geoEvent || {};
    return {
      ...geoEventWithFormValues,
      name,
      address,
      data: {
        ...(geoEventWithFormValues.data || {}),
        acreage,
        containment,
        isComplexParent,
        isFps,
        isPrescribed,
      },
      notificationType,
      isActive,
      lat,
      lng,
    } as Partial<Wildfire>;
  }, [
    acreage,
    address,
    containment,
    geoEvent,
    isActive,
    isComplexParent,
    isFps,
    isPrescribed,
    lat,
    lng,
    name,
    notificationType,
  ]);

  return (
    <Map
      center={defaultMapCenter}
      disableMapLayers={DISABLE_MAP_LAYERS_FOR_FORMS}
      withPlaces={false}
      searchEnabled
      searchBarReturnLink={searchBarReturnLink}
      cursor="crosshair"
      onLocationChange={handleLocationChange}
      searchApi="google"
      zoom={initialZoom}
      onViewportChange={onViewportChange}
      noControls
      layersControlEnabled
      zoomControlEnabled={zoomControlEnabled}
    >
      <StructuredEvacuationsEditLayer
        visible={isEvacZonesLayerVisible}
        evacZones={evacZones}
        onClick={temporarilyDisplayEvacZones ? undefined : handleEvacZoneClick}
        cacheBuster={cacheBuster}
      />
      <AlertCamerasLayer
        visible
        interactive={false}
        withLabels
        withRealtimeUpdates={false}
        // assume 'provider' key means this is a camera since we don't have
        // a better way to explicitly check the type
        selectedCameraId={media && 'provider' in media ? media.id : undefined}
      />
      <GeoEventsLayer
        isFadable
        geoEvents={wildfireEvents}
        selectedGeoEventId={geoEvent?.id}
        isEdit
      />
      <GeoEventMarker
        geoEvent={currentGeoEvent}
        onChange={handleLocationChange}
      />
      <Map.MapEvents onViewportChange={onViewportChange} />
    </Map>
  );
};
